import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { UserPlus, Loader2, Mail } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY = { name: "", email: "", phone: "", password: "" };
const inp = "w-full rounded-lg bg-ivory dark:bg-navy-900 border border-slate-200 dark:border-white/10 px-3.5 py-2.5 text-sm text-navy-900 dark:text-white outline-none focus:border-gold-500";

const OwnerForm = ({ onCreated }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/owners", f);
      if (data.welcome_email?.status === "sent") {
        toast.success(`Owner account created for ${data.email}. Welcome email sent.`);
      } else {
        toast.warning(`Owner account created for ${data.email}, but the welcome email could not be sent. Use Resend welcome email.`, { duration: 10000 });
      }
      setF(EMPTY);
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="owner-create-form">
      <input className={inp} required placeholder="Full name" value={f.name} onChange={set("name")} data-testid="owner-create-name" />
      <input className={inp} required type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="owner-create-email" />
      <input className={inp} placeholder="Phone (optional)" value={f.phone} onChange={set("phone")} data-testid="owner-create-phone" />
      <input className={inp} required type="password" minLength={10} placeholder="Temporary password (10+ chars)" value={f.password} onChange={set("password")} data-testid="owner-create-password" />
      <button type="submit" disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-full bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold px-5 py-2.5 text-sm disabled:opacity-60 transition-colors" data-testid="owner-create-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Create owner
      </button>
    </form>
  );
};

const STATUS_LABEL = { sent: "Welcome email sent", failed: "Welcome email failed", sending: "Sending…" };

const OwnerRow = ({ o, onDone }) => {
  const [busy, setBusy] = useState(false);
  const st = o.welcome_email?.status;
  const resend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/owners/${encodeURIComponent(o.email)}/welcome-email`);
      if (data.status === "sent") toast.success(`Welcome email sent to ${o.email}`);
      else toast.error(`Welcome email to ${o.email} could not be sent. Please try again later.`);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="py-3 flex flex-wrap items-center justify-between gap-3 text-sm" data-testid={`owner-row-${o.email}`}>
      <span className="text-navy-900 dark:text-white font-medium">{o.name} <span className="text-slate-500 font-normal">· {o.email}</span></span>
      <span className="flex items-center gap-3">
        <span className={`text-xs ${st === "failed" ? "text-red-600" : "text-slate-400"}`} data-testid={`owner-welcome-status-${o.email}`}>
          {STATUS_LABEL[st] || "No welcome email"}{o.created_at ? ` · ${new Date(o.created_at).toLocaleDateString("en-US")}` : ""}
        </span>
        <button type="button" onClick={resend} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full border border-gold-500/50 px-3 py-1.5 text-xs font-semibold text-navy-900 dark:text-white hover:bg-gold-500/10 disabled:opacity-60 transition-colors" data-testid={`owner-resend-welcome-${o.email}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Resend welcome email
        </button>
      </span>
    </li>
  );
};

export const AdminOwners = () => {
  const [owners, setOwners] = useState([]);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/owners");
      setOwners(data);
    } catch (_) {
      toast.error("Could not load owner accounts.");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <section className="mt-10 bg-white dark:bg-navy-800 border border-gold-500/20 rounded-2xl p-6" data-testid="admin-owners-section">
      <h2 className="font-serif text-xl text-navy-900 dark:text-white">Owner accounts</h2>
      <p className="mt-1 mb-5 text-sm text-slate-500 dark:text-slate-400">Create a sign-in for a vessel owner so they can subscribe to a plan. HMC emails them a welcome message with their sign-in link (never the password) — share the temporary password with them directly.</p>
      <OwnerForm onCreated={load} />
      <ul className="mt-5 divide-y divide-slate-100 dark:divide-white/5" data-testid="owner-list">
        {owners.length === 0 && <li className="py-3 text-sm text-slate-500" data-testid="owner-list-empty">No owner accounts yet.</li>}
        {owners.map((o) => <OwnerRow key={o.email} o={o} onDone={load} />)}
      </ul>
    </section>
  );
};
